import { useState, useEffect, useCallback } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";
import type { Account, AccountsResponse } from "../types";

interface UseAccountsResult {
  accounts: Account[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch accounts for the current tenant.
 */
export function useAccounts(): UseAccountsResult {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  useEffect(() => {
    const fetchAccounts = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await apiFetch.get<AccountsResponse>("/api/accounts");
        setAccounts(data.accounts);
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err);
        } else {
          setError(err instanceof Error ? err : new Error("Unknown error"));
        }
        setAccounts([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAccounts();
  }, [fetchKey]);

  const refetch = useCallback(() => setFetchKey((k) => k + 1), []);

  return {
    accounts,
    isLoading,
    error,
    refetch,
  };
}
